import React, { Component } from 'react';
import TypingArea from './TypingArea';
import TypingAreaBlock from './TypingAreaBlock';
import Timer from './Timer';
import InputHandler from './InputHandler';
import Result from './Result';
import Settings from './Settings';
import ColorSettings from './ColorSettings';
import Leaderboard from './Leaderboard';
import words from './words.json';
import './styles/Game.css';
import refresh from '../images/refresh-ccw.svg';
import minus from '../images/minus.svg';
import clock from '../images/clock.svg';
import menu from '../images/menu.svg';
import rgb from '../images/rgb.svg';
import { addLeaderboardEntry, getLeaderboard } from './Firebase';

function getRandomWord(maxLength = 10) {
    const word = words[Math.floor(Math.random() * words.length)];
    if (word.length <= maxLength) {
        return word.toLowerCase();
    }
    return getRandomWord(maxLength);
}

function getRandomSentence() {
    let sentence = [];
    const numberOfWords = 100;
    for (let i = 0; i < numberOfWords; i++) {
        const word = getRandomWord();
        sentence.push(word);
    }
    // make sure there are no duplicate words
    sentence = sentence.filter((word, index) => sentence.indexOf(word) === index);
    sentence = sentence.join(' ');
    return sentence;
}

class Game extends Component {
    constructor(props) {
        super(props);
        const sentence = getRandomSentence();
        this.activeTimer = null;
        this.state = {
            sentence: sentence,
            remainingSentence: sentence,
            cursorPosition: 0,
            time: 30,
            timeSetting: 30,
            timer: null,
            attempt: '',
            previousAttemptWPM: 0,
            gameStarted: false,
            accuracy: 0,
            wpm: 0,
            showSettings: false,
            showColorSettings: false,
            alignment: 'block',
            colors: {
                correct: '#7FB069',
                incorrect: '#C83E4D',
                highlight: '#F9A03F',
            },
            submitted: false,
            leaderboard: [],
            highlightedEntry: null,
        };
    }

    componentDidMount() {
        this.safeLoadLeaderboard();
    }

    componentWillUnmount() {
        this.stopTimer(false);
    }

    stopTimer = (updateState = true) => {
        if (this.activeTimer) {
            clearInterval(this.activeTimer);
            this.activeTimer = null;
        }

        if (!updateState) {
            return;
        }

        this.setState((prevState) => {
            if (prevState.timer === null && !prevState.gameStarted) {
                return null;
            }

            return {
                timer: null,
                gameStarted: false,
            };
        });
    };

    startGame = () => {
        if (this.state.gameStarted || this.state.timer || this.activeTimer || this.state.time === 0) {
            return;
        }

        const timer = setInterval(() => {
            let shouldFinish = false;

            this.setState((prevState) => {
                if (prevState.time <= 1) {
                    shouldFinish = true;
                    return { time: 0 };
                }

                return { time: prevState.time - 1 };
            }, () => {
                if (shouldFinish) {
                    this.stopTimer();
                    this.showResults();
                }
            });
        }, 1000);
        this.activeTimer = timer;
        this.setState({ timer, gameStarted: true });
    };

    showResults = () => {
        const { sentence, attempt, timeSetting } = this.state;
        const attemptLength = attempt.length;
        const correctChars = attempt.split('').filter((char, index) => char === sentence[index]).length;
        const safeTimeSetting = Number.isFinite(timeSetting) && timeSetting > 0 ? timeSetting : 60;

        // Calculate the factor to adjust the attempt length based on the selected timer.
        const adjustmentFactor = 60 / safeTimeSetting;

        // Calculate the adjusted attempt length based on the time setting.
        const adjustedAttemptLength = attempt.length * adjustmentFactor;

        // Calculate words per minute (WPM) based on the adjusted attempt length.
        const wpm = attemptLength === 0 ? 0 : Math.round(adjustedAttemptLength / 5);

        // Calculate accuracy and keep it in [0, 100].
        const rawAccuracy = attemptLength === 0 ? 0 : (correctChars / attemptLength) * 100;
        const accuracy = Math.max(0, Math.min(100, rawAccuracy));

        const newHighlightedEntry = {
            user: '',
            wpm,
            accuracy,
        };

        this.setState({
            accuracy,
            wpm,
            previousAttemptWPM: wpm,
            highlightedEntry: newHighlightedEntry,
        });
    };

    reset = () => {
        this.stopTimer();
        const sentence = getRandomSentence();
        this.setState({
            sentence: sentence,
            remainingSentence: sentence,
            cursorPosition: 0,
            time: this.state.timeSetting,
            timer: null,
            attempt: '',
            accuracy: 0,
            wpm: 0,
            gameStarted: false,
            submitted: false,
        });
      };

    toggleAlignment = () => {
        const alignment = this.state.alignment === 'line' ? 'block' : 'line';
        this.reset();
        this.setState({ alignment });
    };

    toggleSettings = () => {
        this.setState((prevState) => {
            return {
                showSettings: !prevState.showSettings,
                showColorSettings: false,
            };
        });
    };

    toggleColorSettings = () => {
        this.setState((prevState) => {
            return {
                showSettings: false,
                showColorSettings: !prevState.showColorSettings 
            };
        });
    };

    onTimeChange = (value) => {
        const time = parseInt(value);
        this.setState({ time, timeSetting: time });
    };

    handleColorChange = (color, index) => {
        const colors = { ...this.state.colors };
        colors[index] = color;
        this.setState({ colors });
    };

    setLeaderboard = (leaderboard) => {
        this.setState({ leaderboard });
    };

    safeLoadLeaderboard = () => {
        try {
            return getLeaderboard()
                .then((leaderboard) => {
                    this.setState({
                        leaderboard: Array.isArray(leaderboard) ? leaderboard : [],
                    });
                })
                .catch((error) => {
                    console.error('Failed to load leaderboard:', error);
                    this.setState({ leaderboard: [] });
                });
        } catch (error) {
            console.error('Failed to load leaderboard:', error);
            this.setState({ leaderboard: [] });
            return null;
        }
    };

    updateLeaderboard = () => {
        return this.safeLoadLeaderboard();
    };

    setHighlightedEntry = (highlightedEntry) => {
        this.setState({ highlightedEntry });
    };

    updateSubmitted = () => {
        this.setState({ submitted: !this.state.submitted });
    };

    isEditableTarget = (target) => {
        if (!target || !target.tagName) {
            return false;
        }

        const tagName = target.tagName.toUpperCase();
        return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT' || target.isContentEditable === true;
    };

    isTypingKey = (event) => {
        return event.key === 'Backspace' || event.key === ' ' || event.key.length === 1;
    };

    isStartKey = (event) => {
        return event.key === ' ' || event.key.length === 1;
    };

    handleKeyDown = (e) => {
        const { time, gameStarted } = this.state;

        if (time === 0) {
            return;
        }

        if (this.isEditableTarget(e.target)) {
            return;
        }

        if (e.ctrlKey || e.metaKey || e.altKey) {
            return;
        }

        if (e.key === 'Enter') {
            return;
        }

        if (!this.isTypingKey(e)) {
            return;
        }

        if (e.key === ' ') {
            e.preventDefault();
        }

        if (!gameStarted && this.isStartKey(e)) {
            this.startGame();
        }

        this.setState((prevState) => {
            const { alignment, attempt, cursorPosition, sentence } = prevState;

            if (e.key === 'Backspace') {
                if (attempt.length === 0) {
                    return null;
                }

                const nextAttempt = attempt.slice(0, -1);
                const remainingSentence = sentence.slice(nextAttempt.length);
                const nextCursorPosition = alignment === 'block'
                    ? Math.max(0, cursorPosition - 1)
                    : cursorPosition;

                return {
                    remainingSentence,
                    attempt: nextAttempt,
                    cursorPosition: nextCursorPosition,
                };
            }

            if (attempt.length >= sentence.length) {
                return null;
            }

            const nextAttempt = attempt + e.key;
            const remainingSentence = sentence.slice(nextAttempt.length);
            const nextCursorPosition = alignment === 'block'
                ? Math.min(sentence.length, cursorPosition + 1)
                : cursorPosition;

            return {
                remainingSentence,
                cursorPosition: nextCursorPosition,
                attempt: nextAttempt,
            };
        });
    };

    handleKeyUp = (e) => {
        if (e.key === ' ' && !this.isEditableTarget(e.target)) {
            e.preventDefault();
        }
    };

    render() {
        const { alignment, previousAttemptWPM, gameStarted, time } = this.state;
        const runActive = gameStarted && time > 0;
        
        return (
            <div className="game">
                <div className="utils-container">
                    <Timer time={this.state.time} colors={this.state.colors} />
                    <div className="button-container">
                        <button className="reset-button" onClick={this.reset}>
                            <img src={refresh} alt="reset" />
                        </button>
                        <button className="time-button" onClick={this.toggleSettings}>
                            <img src={clock} alt="settings" />
                        </button>
                        <button className="align-button" onClick={this.toggleAlignment}>
                            <img src={alignment === 'line' ? menu : minus} alt="align" />
                        </button>
                        <button className="color-button" onClick={this.toggleColorSettings}>
                            <img src={rgb} alt="color" />
                        </button>
                    </div>
                    <Settings show={this.state.showSettings} time={this.state.time} onTimeChange={this.onTimeChange} onColorChange={this.onColorChange} />
                    <ColorSettings show={this.state.showColorSettings} colors={this.state.colors} onColorChange={this.handleColorChange} />
                </div>
                <div className="main-container">
                    {alignment === 'line' ? (
                        <TypingArea
                            sentence={this.state.sentence}
                            remainingSentence={this.state.remainingSentence}
                            cursorPosition={this.state.cursorPosition}
                            attempt={this.state.attempt}
                            previousAttemptWPM={previousAttemptWPM}
                            colors={this.state.colors}
                        />
                    ) : (
                        <TypingAreaBlock
                            sentence={this.state.sentence}
                            cursorPosition={this.state.cursorPosition}
                            attempt={this.state.attempt}
                            previousAttemptWPM={previousAttemptWPM}
                            runActive={runActive}
                            colors={this.state.colors}
                        />
                    )}
                    {this.state.time === 0 && (
                        <>
                            <Result accuracy={this.state.accuracy} wpm={this.state.wpm} submitScore={addLeaderboardEntry} submitted={this.state.submitted} updateSubmitted={this.updateSubmitted} updateLeaderboard={this.updateLeaderboard} setHighlightedEntry={this.setHighlightedEntry} />
                        </>
                    )}
                    <Leaderboard leaderboard={this.state.leaderboard} updateLeaderboard={this.setLeaderboard} highlightedEntry={this.state.highlightedEntry} colors={this.state.colors} />
                </div>

                <div style={{ height: '10vh' }}></div>

                <div className="footer">
                    <div className="footer-text">
                        <p>
                            By <a className="footer-link" href='http://silasn.com'> Silas Nevstad</a>
                        </p>
                    </div>
                </div>
                <InputHandler onKeyDown={this.handleKeyDown} onKeyUp={this.handleKeyUp} />
            </div>
        );
    }
}

export default Game;
